import React, { useContext } from 'react'

import {
  Box,
  ButtonPopover,
  DateInput,
  Grid,
  Loader,
  makeAppStyles,
  ReportRange,
  RoundedPlainTextButton,
  Typography,
} from '@percept/mui'

import { ArrowDropDown, ChevronRight } from '@percept/mui/icons'

import { DashboardCard } from 'components/DashboardCard'

import { AggregatedInsightUnits } from 'components/AggregatedInsightUnits'

import { BubbleChart, BubbleChartHorizontal, BubbleChartProps } from './BubbleChart'

import { OverallSetupScoreSummary } from './OverallSetupScoreSummary'

import { OrgUnitContext, ReportProviderContext, TimeseriesContext } from '@percept/app-components'

import { providerLabelMap } from '@percept/constants'
import { CampaignSetupContext } from './context'
import { isoDateUTC } from '@percept/utils'


const useStyles = makeAppStyles( theme => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    height: 28,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('lg')]: {
      height: 24,
      fontSize: 20,
    },
    [theme.breakpoints.down('xs')]: {
      height: 'unset',
      fontSize: 18,
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
  },
  titleProvider: {
    color: theme.palette.text.secondary,
  },
  titleTextContent: {
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  dateRange: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  titleSeparator: {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 0.25),
    fontSize: 30,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    }
  },
  cardTitleProvider: {
    marginRight: theme.spacing(1),
  },
  categoryLabel: {
    fontSize: 13,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
  orgLink: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.spacing(2),
  },
  health: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing(2),
  },
}))


export type CampaignSetupViewProps<Datum extends object, Key extends string> = {
  pageTitle?: React.ReactNode
  cardTitle: React.ReactNode
  tooltipContent?: React.ReactNode
  isError: boolean
  isLoading: boolean
  isRefetching: boolean
  dataSignal: boolean
} & BubbleChartProps<Datum, Key>

export function CampaignSetupView<Datum extends object, Key extends string>({
  pageTitle,
  cardTitle,
  tooltipContent,
  isError,
  isLoading,
  isRefetching,
  dataSignal,
  ...props
}: CampaignSetupViewProps<Datum, Key>) {

  const orgUnit = useContext(OrgUnitContext)
  const provider = useContext(ReportProviderContext)
  const { start, end, minDate, maxDate } = useContext(TimeseriesContext)
  const [, setState] = useContext(CampaignSetupContext)

  const classes = useStyles()

  pageTitle = pageTitle || `${orgUnit.name} Campaign Setup`

  return (
    <Box p={3}>
      <Typography variant='h4' className={classes.title}>
        <div className={classes.titleTextContent}>
          { pageTitle }
          { !!provider && (
            <>
              <ChevronRight className={classes.titleSeparator} />
              <span className={classes.titleProvider}>
                { providerLabelMap[provider] }
              </span>
            </>
          )}
        </div>
        { !!(start && end) && (
          <ButtonPopover
            className={classes.dateRange}
            ButtonComponent={RoundedPlainTextButton}
            variant='contained'
            size='small'
            endIcon={<ArrowDropDown />}
            buttonContent={
              <ReportRange
                disableTypography
                start={start}
                end={end} />
            }>
            { ({ onClose }) => {
              return (
                <DateInput
                  minDate={minDate}
                  maxDate={maxDate}
                  variant='static'
                  value={end}
                  onChange={(date) => {
                    if( date ){
                      setState({ referenceDate: isoDateUTC(new Date(date)) })
                      onClose()
                    }
                  }} />
              )
            }}
          </ButtonPopover>
        )}
      </Typography>

      <Grid container spacing={5}>
        <Grid item xs={12} md={8} xl={9}>
          <DashboardCard
            title={cardTitle}
            tooltipContent={tooltipContent}
            isRefetching={isRefetching}>
            <Box p={5}>
              { isError && (
                <Box
                  py={8}
                  fontSize={18}
                  color='text.secondary'
                  display='flex'
                  justifyContent='center'>
                  An error occurred and the data could not be loaded
                </Box>
              )}

              { isLoading && <Loader preset='fullsize' minHeight={300} /> }

              {/* { dataSignal && <BubbleChart {...props} /> } */}
              { dataSignal && <BubbleChartHorizontal {...props} /> }
            </Box>
          </DashboardCard>
        </Grid>

        <Grid item xs={12} md={4} xl={3}>
          <Grid container spacing={5}>
            <Grid item xs={12} sm={6} md={12}>
              <OverallSetupScoreSummary />
            </Grid>
            <Grid item xs={12} sm={6} md={12}>
              <AggregatedInsightUnits />
            </Grid>
          </Grid>
        </Grid>

      </Grid>

    </Box>
  )
}
